<script>
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { fly } from "svelte/transition";
  import { Modal as DangerModal } from "carbon-components-svelte";
  import { useNavigate } from "svelte-navigator";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { projectListStore } from "@stores/damProjectsStore.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import { ROUTES } from "@utils/routes.js";
  import Header from "@components/Header.svelte";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import {
    Button,
    InfoBlock,
    TextArea,
    Tooltip,
    SectionWrapper,
  } from "paxray-components-svelte";
  import PhaseMessageLanguage from "../components/PhaseMessageLanguage.svelte";
  import {
    END_TASK_REGISTRATION_MESSAGES,
    END_TASK_REGISTRATION_PAUSE_MESSAGES,
  } from "./phaseMessages.js";

  const { markStepAsCompleted } = projectListStore;
  const urlParams = useUrlParams();
  const navigate = useNavigate();
  const step = PROJECT_STAGES.TASK_REGISTRATION.steps.END_TASK_REG;
  const stage = PROJECT_STAGES.TASK_REGISTRATION.id;

  let stepActionsRef;
  let openDangerModal = false;

  $: ({ idproject } = $urlParams);
  $: ({ currentProjectContacts } = $projectListStore);

  let language = "en";
  let message = END_TASK_REGISTRATION_MESSAGES.en;
  let pauseMessage = END_TASK_REGISTRATION_PAUSE_MESSAGES.en;
  let editingMessage = false;
  let showRegistrationGuide = false;
  let registrationGuideStep = 0;
  let registrationGuideSteps = [];

  onMount(() =>
    registerViewTour(() => {
      if (openDangerModal) return;
      const completed = Boolean(
        document.querySelector(
          '[data-guide="end-registration-action"] .completed-badge',
        ),
      );
      registrationGuideSteps = [
        {
          target: '[data-guide="end-registration-overview"]',
          title: "Prepare to end task registration",
          text: "End this phase when your team has finished registering the tasks needed for your project. Review the participant message and pause message before continuing.",
        },
        {
          target:
            '[data-guide="end-registration-language"] .phase_message_language',
          title: "Choose the participant language",
          text: "Choose the language for both messages. Changing the language replaces them with the corresponding templates, so choose it before making edits.",
        },
        {
          target: '[data-guide="end-registration-message"]',
          title: "Review the participant message",
          text: "This message is shown to participants when task registration ends. Click “Edit message” to adapt it to your project and explain what happens next.",
        },
        {
          target: '[data-guide="end-registration-pause"]',
          title: "Check the pause message",
          text: "This text appears in the Paxray client menu while data collection is paused. Use “Edit message” to change it. Leave it empty to use the default text.",
        },
        {
          target:
            '[data-guide="end-registration-action"] > button, [data-guide="end-registration-action"] > .completed-badge',
          title: completed
            ? "Task registration has ended"
            : "End task registration",
          text: completed
            ? "This phase is complete. Return to Project Progress to review the remaining Discovery steps and continue with your project."
            : "Check both messages, then click “End Task Registration” and confirm. This ends the phase, stops the current data collection and shows the participant message. This action cannot be undone.",
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      registrationGuideStep = 0;
      showRegistrationGuide = registrationGuideSteps.length > 0;
    }),
  );

  async function endTaskRegistration() {
    const response = await fetchWithSession(
      "/api/damprojects/endTaskRegistration",
      {
        method: "POST",
        body: JSON.stringify({ idproject, message, pauseMessage }),
      },
    );
    const data = await response.json();

    if (data?.ok) {
      const completion = await markStepAsCompleted(idproject, step, stage);
      if (completion?.ok) {
        stepActionsRef?.refresh();
        navigate(`${ROUTES.dam.progress}?idproject=${idproject}`);
      }
    } else {
      console.error(data?.error || "Task Registration could not be ended.");
    }

    openDangerModal = false;
  }
</script>

<Header
  section="Project"
  area="Discovery"
  breadcrumb="End Task Registration"
  tooltipText="Review the participant message before ending the task registration phase."
>
  <div data-guide="end-registration-action">
    <ProjectStepActions
      bind:this={stepActionsRef}
      {idproject}
      {step}
      {stage}
      showGoBackButton={false}
      text="End Task Registration"
      size="sm"
      confirmBeforeComplete={false}
      onClick={() => (openDangerModal = true)}
    />
  </div>
</Header>

<SectionWrapper fullscreen={true} seamless={true} seamlessPadding={true}>
  <div class="main" slot="main">
    <div data-guide="end-registration-overview">
      <InfoBlock
        type="info"
        heading="End Task Registration"
        message="Review the message users will see after task registration. When you are ready, click &quot;End Task Registration&quot; to complete this transition and continue with the project setup."
        showClose={false}
      />
    </div>
    <div class="row">
      <div class="cell">
        <div class="field_label" data-guide="end-registration-language">
          <PhaseMessageLanguage
            messages={END_TASK_REGISTRATION_MESSAGES}
            consents={END_TASK_REGISTRATION_PAUSE_MESSAGES}
            bind:language
            bind:value={message}
            bind:consent={pauseMessage}
          />
          <Button
            size="field"
            kind="secondary"
            on:click={() => (editingMessage = !editingMessage)}
          >
            {editingMessage ? "Done editing" : "Edit message"}
          </Button>
        </div>
        <div data-guide="end-registration-message">
          {#if editingMessage}
            <TextArea
              placeholder="Enter a message for the users"
              bind:value={message}
              richText
              persons={currentProjectContacts}
            />
          {:else}
            <div class="message-preview">
              {@html message}
            </div>
          {/if}
        </div>
        <div class="pause_message" data-guide="end-registration-pause">
          {#if editingMessage}
            <TextArea
              id="task-registration-pause-message"
              labelText="Pause Message"
              hideLabel
              placeholder="Enter the text shown while recording is paused"
              rows={3}
              bind:value={pauseMessage}
              tooltipText="Plain text shown in the recorder menu while recording is paused. Leave empty for the recorder's default text."
            />
          {:else}
            <div class="field_label">
              <label class="bx--label" for="task-registration-pause-message"
                >Pause Message</label
              >
              <Tooltip
                text="Plain text shown in the recorder menu while recording is paused. Leave empty for the recorder's default text."
              />
            </div>
            <p class="pause-preview">
              {pauseMessage || "The recorder's default text will be shown."}
            </p>
          {/if}
        </div>
      </div>
    </div>
  </div>
</SectionWrapper>

<SpotlightGuide
  bind:open={showRegistrationGuide}
  bind:currentStep={registrationGuideStep}
  steps={registrationGuideSteps}
  allowInteraction={false}
/>

<DangerModal
  danger
  bind:open={openDangerModal}
  preventCloseOnClickOutside
  modalHeading="End Task Registration?"
  primaryButtonText="End Task Registration"
  secondaryButtonText="Cancel"
  on:click:button--primary={endTaskRegistration}
  on:click:button--secondary={() => (openDangerModal = false)}
>
  <p>
    Ending Task Registration will stop the current recording and show the
    configured message to participants. This action cannot be undone.
  </p>
</DangerModal>

<style>
  .field_label {
    display: flex;
    align-items: flex-end;
    gap: var(--xs);
    margin-bottom: var(--control-gap);
  }

  .field_label .bx--label {
    margin: 0;
  }

  .field_label :global(.phase_message_language) {
    margin-left: 0;
  }

  .message-preview,
  .pause-preview {
    padding: 1rem;
    border: 1px solid var(--border-color);
    border-radius: 0.25rem;
    overflow-wrap: anywhere;
  }

  .pause-preview {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
  }

  .pause_message {
    flex: 0 0 auto;
    margin-top: var(--control-gap);
  }

  .pause_message :global(.bx--text-area) {
    background: transparent;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
  }

  .main {
    max-width: 100%;
    width: 100%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: var(--control-gap);
  }

  .row {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
  }

  .cell {
    flex: 1 1 auto;
    width: 50%;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }

  .message-preview :global(b),
  .message-preview :global(strong) {
    font-weight: bold;
  }

  .message-preview :global(i),
  .message-preview :global(em) {
    font-style: italic;
  }

  .message-preview :global(u) {
    text-decoration: underline;
  }
</style>
